<template>
  <div class="auth-container">
    <div class="auth-card">
      <!-- Logo Section -->
      <div class="auth-logo">
        <img src="@/assets/logo.jpg" alt="Gypsy Souls" class="auth-logo-img" />
        <p>Admin Portal</p>
      </div>

      <!-- Login Form -->
      <form @submit.prevent="handleLogin">
        <div class="mb-3">
          <label for="email" class="form-label">Email Address</label>
          <input
            type="email"
            class="form-control"
            id="email"
            v-model="form.email"
            :class="{ 'is-invalid': errors.email }"
            placeholder="Enter your email"
            required
            autocomplete="off"
          />
          <div v-if="errors.email" class="invalid-feedback">
            {{ errors.email }}
          </div>
        </div>

        <div class="mb-3">
          <label for="password" class="form-label">Password</label>
          <div class="input-group">
            <input
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              id="password"
              v-model="form.password"
              :class="{ 'is-invalid': errors.password }"
              placeholder="Enter your password"
              required
              autocomplete="new-password"
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="togglePasswordVisibility"
            >
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <div v-if="errors.password" class="invalid-feedback">
            {{ errors.password }}
          </div>
        </div>

        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger" role="alert">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="btn btn-primary w-100"
          :disabled="isLoading"
        >
          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          >
            <span class="visually-hidden">Loading...</span>
          </span>
          <i v-else class="fas fa-sign-in-alt me-2"></i>
          {{ isLoading ? "Signing In..." : "Sign In" }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "Login",
  data() {
    return {
      form: {
        email: "",
        password: "",
      },
      errors: {},
      error: null,
      isLoading: false,
      showPassword: false,
    };
  },

  methods: {
    ...mapActions("auth", ["login"]),

    validateForm() {
      this.errors = {};

      if (!this.form.email) {
        this.errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(this.form.email)) {
        this.errors.email = "Please enter a valid email address";
      }

      if (!this.form.password) {
        this.errors.password = "Password is required";
      } else if (this.form.password.length < 6) {
        this.errors.password = "Password must be at least 6 characters";
      }

      return Object.keys(this.errors).length === 0;
    },

    async handleLogin() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const result = await this.login(this.form);

        // Show success message
        await Swal.fire({
          title: "Login Successful!",
          text: result.message,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // Redirect to admin dashboard
        this.$router.push("/admin/dashboard");
      } catch (error) {
        this.error = error.message;

        // Show error toast
        Swal.fire({
          title: "Login Failed",
          text: error.message,
          icon: "error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    clearForm() {
      // Clear any browser-saved values on component creation only
      this.form.email = "";
      this.form.password = "";
      this.errors = {};
      this.error = null;
    },
  },

  created() {
    // Ensure form is completely empty on component creation
    this.clearForm();
  },
};
</script>

<style scoped>
.auth-container {
  background: linear-gradient(135deg, #b46428, #78501e);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.auth-card {
  background: #f5f2ed;
  border-radius: var(--border-radius);
  box-shadow: 0 10px 30px rgba(44, 36, 25, 0.15);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
}

.auth-logo {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-logo-img {
  height: 80px;
  width: auto;
  max-width: 300px;
  object-fit: contain;
  margin-bottom: 1rem;
  transition: transform 0.3s ease;
}

.auth-logo-img:hover {
  transform: scale(1.05);
}

.auth-logo h1 {
  color: #b46428;
  font-weight: 700;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.auth-logo p {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
}

.form-control:focus {
  border-color: #b46428;
  box-shadow: 0 0 0 0.2rem rgba(180, 100, 40, 0.25);
}

.btn-primary {
  background-color: #b46428;
  border-color: #b46428;
  font-weight: 600;
  padding: 0.75rem;
}

.btn-primary:hover {
  background-color: #966428;
  border-color: #966428;
}

.btn-outline-secondary {
  border-left: none;
}

.input-group .form-control:focus {
  z-index: 2;
}

@media (max-width: 576px) {
  .auth-card {
    margin: 1rem;
    padding: 1.5rem;
  }

  .auth-logo-img {
    height: 60px;
    max-width: 250px;
  }

  .auth-logo h1 {
    font-size: 1.75rem;
  }
}
</style>
